import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchApplicationsByStatusPage as apiFetchApplicationsByStatusPage,
  withFullscreenLoader,
} from "../../services/belediyeApi";

// ------------------------------------------------------------
// Constants
// ------------------------------------------------------------
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_ORDERING = "-created_at";

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
const stableStringify = (obj) => {
  if (!obj || typeof obj !== "object") return String(obj || "");
  const keys = Object.keys(obj).sort();
  const out = {};
  for (const k of keys) out[k] = obj[k];
  return JSON.stringify(out);
};

const normalizeFilters = (filters) => {
  const f = filters && typeof filters === "object" ? filters : {};
  const cleaned = {};

  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    cleaned[k] = v;
  }

  return cleaned;
};

const makeCacheKey = ({ status, page, pageSize, filters }) => {
  const clean = normalizeFilters(filters);
  return stableStringify({
    status: status || "",
    page: Number(page || 1),
    page_size: Number(pageSize || DEFAULT_PAGE_SIZE),
    filters: clean,
  });
};

const buildRequestConfigFromLoader = (loader) => {
  if (!loader || loader.variant !== "fullscreen") return {};

  return withFullscreenLoader({
    title: loader.title || "Başvurular Yükleniyor",
    subtitle: loader.subtitle || "Liste hazırlanıyor...",
  });
};

// ------------------------------------------------------------
// Thunks
// ------------------------------------------------------------

/**
 * Yeni ana thunk:
 * status + page + filters bazlı liste çeker
 *
 * payload:
 * {
 *   status: "yeni" | "işleme_alındı" | "çözüldü" | "çözülemedi" | "",
 *   page: 1,
 *   pageSize: 50,
 *   filters: { q, neighborhood, date_from, date_to, ... },
 *   loader: { variant: "fullscreen", title, subtitle } | null
 * }
 */
export const fetchApplicationsPage = createAsyncThunk(
  "belediyeApplications/fetchApplicationsPage",
  async (
    {
      status = "",
      page = 1,
      pageSize = DEFAULT_PAGE_SIZE,
      filters = {},
      loader = null,
    } = {},
    { rejectWithValue, signal }
  ) => {
    try {
      const cleanFilters = normalizeFilters(filters);

      const params = {
        ...cleanFilters,
        status: status || "",
        page,
        pageSize,
        ordering: cleanFilters.ordering || DEFAULT_ORDERING,
      };

      const requestConfig = buildRequestConfigFromLoader(loader);

      const res = await apiFetchApplicationsByStatusPage(params, requestConfig);

      if (signal?.aborted) {
        return rejectWithValue("İstek iptal edildi.");
      }

      const key = makeCacheKey({
        status,
        page,
        pageSize,
        filters: cleanFilters,
      });

      return {
        key,
        status: status || "",
        page,
        pageSize,
        filters: cleanFilters,
        params,
        items: Array.isArray(res?.items) ? res.items : [],
        count: Number(res?.count ?? 0),
        next: res?.next ?? null,
        previous: res?.previous ?? null,
      };
    } catch (err) {
      return rejectWithValue("Başvurular alınamadı.");
    }
  }
);

/**
 * Backward compatible thunk
 * Eski sayfalar dispatch(fetchApplicationsByStatus(status)) kullanıyorsa
 * page=1 ve pageSize=50 ile devam eder.
 */
export const fetchApplicationsByStatus = createAsyncThunk(
  "belediyeApplications/fetchApplicationsByStatus",
  async (status, { dispatch, rejectWithValue }) => {
    try {
      const action = await dispatch(
        fetchApplicationsPage({
          status,
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          filters: {},
          loader: null,
        })
      );

      if (fetchApplicationsPage.rejected.match(action)) {
        return rejectWithValue(action.payload || "Başvurular alınamadı.");
      }

      return action.payload;
    } catch (e) {
      return rejectWithValue("Başvurular alınamadı.");
    }
  }
);

// ------------------------------------------------------------
// Slice
// ------------------------------------------------------------
const belediyeApplicationsSlice = createSlice({
  name: "belediyeApplications",
  initialState: {
    // Legacy alanlar
    applications: [],
    loading: false,
    error: null,

    // Yeni pagination meta
    pagination: {
      count: 0,
      next: null,
      previous: null,
    },

    // Yeni cache
    pagesByKey: {},

    // Aktif query bilgisi
    current: {
      status: "",
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      filters: {},
      key: "",
    },

    // Son istek takibi
    lastRequestedKey: "",
  },
  reducers: {
    clearApplications: (state) => {
      state.applications = [];
      state.loading = false;
      state.error = null;
      state.pagination = {
        count: 0,
        next: null,
        previous: null,
      };
      state.pagesByKey = {};
      state.current = {
        status: "",
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        filters: {},
        key: "",
      };
      state.lastRequestedKey = "";
    },

    setApplicationsQuery: (state, action) => {
      const { status, page, pageSize, filters } = action.payload || {};

      state.current.status = status ?? state.current.status;
      state.current.page = page ?? state.current.page;
      state.current.pageSize = pageSize ?? state.current.pageSize;
      state.current.filters = normalizeFilters(filters ?? state.current.filters);

      state.current.key = makeCacheKey({
        status: state.current.status,
        page: state.current.page,
        pageSize: state.current.pageSize,
        filters: state.current.filters,
      });
    },

    /**
     * Cache’ten anında legacy alanlara veri basar.
     * Sayfa geri açıldığında anında render için faydalı.
     */
    hydrateApplicationsFromCache: (state, action) => {
      const {
        status = "",
        page = 1,
        pageSize = DEFAULT_PAGE_SIZE,
        filters = {},
      } = action.payload || {};

      const cleanFilters = normalizeFilters(filters);
      const key = makeCacheKey({
        status,
        page,
        pageSize,
        filters: cleanFilters,
      });

      const cached = state.pagesByKey[key];

      state.current = {
        status,
        page,
        pageSize,
        filters: cleanFilters,
        key,
      };

      if (!cached) return;

      state.applications = cached.items;
      state.pagination = {
        count: cached.count,
        next: cached.next,
        previous: cached.previous,
      };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplicationsPage.pending, (state, action) => {
        const arg = action.meta.arg || {};
        const cleanFilters = normalizeFilters(arg.filters || {});
        const key = makeCacheKey({
          status: arg.status || "",
          page: arg.page || 1,
          pageSize: arg.pageSize || DEFAULT_PAGE_SIZE,
          filters: cleanFilters,
        });

        state.lastRequestedKey = key;
        state.loading = true;
        state.error = null;

        state.current = {
          status: arg.status || "",
          page: arg.page || 1,
          pageSize: arg.pageSize || DEFAULT_PAGE_SIZE,
          filters: cleanFilters,
          key,
        };
      })
      .addCase(fetchApplicationsPage.fulfilled, (state, action) => {
        const p = action.payload;

        state.pagesByKey[p.key] = {
          items: p.items,
          count: p.count,
          next: p.next,
          previous: p.previous,
          fetchedAt: Date.now(),
          status: p.status,
          page: p.page,
          pageSize: p.pageSize,
          filters: p.filters,
          params: p.params,
        };

        if (state.lastRequestedKey === p.key) {
          state.applications = p.items;
          state.pagination = {
            count: p.count,
            next: p.next,
            previous: p.previous,
          };
          state.current = {
            status: p.status,
            page: p.page,
            pageSize: p.pageSize,
            filters: p.filters,
            key: p.key,
          };
        }

        state.loading = false;
        state.error = null;
      })
      .addCase(fetchApplicationsPage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Başvurular alınamadı.";
      });

    builder
      .addCase(fetchApplicationsByStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationsByStatus.fulfilled, (state, action) => {
        const p = action.payload;

        if (!p || !Array.isArray(p.items)) {
          state.loading = false;
          state.applications = [];
          state.pagination = {
            count: 0,
            next: null,
            previous: null,
          };
          state.error = null;
          return;
        }

        state.pagesByKey[p.key] = {
          items: p.items,
          count: p.count,
          next: p.next,
          previous: p.previous,
          fetchedAt: Date.now(),
          status: p.status,
          page: p.page,
          pageSize: p.pageSize,
          filters: p.filters,
          params: p.params,
        };

        state.applications = p.items;
        state.pagination = {
          count: p.count,
          next: p.next,
          previous: p.previous,
        };
        state.loading = false;
        state.error = null;

        state.current = {
          status: p.status,
          page: p.page,
          pageSize: p.pageSize,
          filters: p.filters || {},
          key: p.key,
        };
        state.lastRequestedKey = p.key;
      })
      .addCase(fetchApplicationsByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Başvurular alınamadı.";
      });
  },
});

// ------------------------------------------------------------
// Exports
// ------------------------------------------------------------
export const {
  clearApplications,
  setApplicationsQuery,
  hydrateApplicationsFromCache,
} = belediyeApplicationsSlice.actions;

// Legacy selector
export const selectApplicationsLegacy = (state) => state.belediyeApplications;

// Yeni selector: cache’ten belirli sayfa
export const selectApplicationsPageFromCache = (
  state,
  { status, page, pageSize, filters }
) => {
  const key = makeCacheKey({ status, page, pageSize, filters });
  return state.belediyeApplications.pagesByKey[key] || null;
};

// Yeni selector: aktif pagination meta
export const selectApplicationsPagination = (state) =>
  state.belediyeApplications.pagination;

// Yeni selector: aktif query
export const selectApplicationsCurrentQuery = (state) =>
  state.belediyeApplications.current;

export default belediyeApplicationsSlice.reducer;